import React, { useState, useRef, useEffect } from 'react';
import {
  Check,
  ShieldCheck,
  Sparkles,
  Eye,
  Info,
  ExternalLink,
  Layers,
  X,
  Clock,
  HelpCircle,
  BarChart3
} from 'lucide-react';
import { TrustLevel, TRUST_LEVEL_INFO, TrustedMetric } from '../services/dataTrustEngine';

export interface MetricSourceBadgeProps {
  trustLevel?: TrustLevel;
  type?: 'apple_public' | 'estimated' | 'developer_verified' | 'observed' | 'provider_estimated' | 'not_available';
  metricData?: Partial<TrustedMetric>;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  showIcon?: boolean;
  interactive?: boolean;
  customLabel?: string;
}

export const MetricSourceBadge: React.FC<MetricSourceBadgeProps> = ({
  trustLevel,
  type,
  metricData,
  size = 'sm',
  className = '',
  showIcon = true,
  interactive = true,
  customLabel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Map legacy type or trustLevel
  let resolvedTrust: TrustLevel = trustLevel || 'PUBLIC_METADATA';
  if (!trustLevel && type) {
    if (type === 'apple_public') resolvedTrust = 'PUBLIC_METADATA';
    else if (type === 'developer_verified') resolvedTrust = 'DEVELOPER_VERIFIED';
    else if (type === 'observed') resolvedTrust = 'APPSTATSVIEW_OBSERVED';
    else if (type === 'provider_estimated') resolvedTrust = 'PROVIDER_ESTIMATED';
    else if (type === 'estimated') resolvedTrust = 'APPSTATSVIEW_ESTIMATED';
    else if (type === 'not_available') resolvedTrust = 'NOT_AVAILABLE';
  }

  const info = TRUST_LEVEL_INFO[resolvedTrust] || TRUST_LEVEL_INFO.PUBLIC_METADATA;

  // Icon resolution
  let icon = <Check className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />;
  if (resolvedTrust === 'DEVELOPER_VERIFIED') {
    icon = <ShieldCheck className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />;
  } else if (resolvedTrust === 'APPSTATSVIEW_OBSERVED') {
    icon = <Eye className="w-3 h-3 text-indigo-600" strokeWidth={2} />;
  } else if (resolvedTrust === 'PROVIDER_ESTIMATED') {
    icon = <Layers className="w-3 h-3 text-purple-600" strokeWidth={2} />;
  } else if (resolvedTrust === 'APPSTATSVIEW_ESTIMATED') {
    icon = <Sparkles className="w-3 h-3 text-blue-600" strokeWidth={2} />;
  } else if (resolvedTrust === 'NOT_AVAILABLE' || resolvedTrust === 'UNVERIFIED') {
    icon = <HelpCircle className="w-3 h-3 text-slate-500" strokeWidth={2} />;
  }

  const sizeClasses =
    size === 'xs'
      ? 'px-2 py-0.5 text-[10px]'
      : size === 'md'
      ? 'px-3 py-1 text-xs'
      : 'px-2.5 py-0.5 text-[11px]';

  // Handle outside click to close popover
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const displayLabel = customLabel || info.label;

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <button
        type="button"
        onClick={(e) => {
          if (interactive) {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }
        }}
        title={`Click for metric provenance: ${info.label}`}
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold border transition-all ${
          interactive ? 'cursor-pointer hover:opacity-90 active:scale-95' : 'cursor-default'
        } ${info.badgeClass} ${sizeClasses} ${className}`}
      >
        {showIcon && icon}
        <span className="truncate max-w-[190px]">{displayLabel}</span>
        {interactive && <Info className="w-2.5 h-2.5 opacity-70 ml-0.5 shrink-0" />}
      </button>

      {/* Popover (Rule 23) */}
      {isOpen && (
        <div
          className="absolute z-50 bottom-full left-0 mb-2 w-72 sm:w-80 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 text-left animate-in fade-in zoom-in-95 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between pb-2 mb-2 border-b border-slate-100">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Metric Provenance
              </span>
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                {metricData?.label || 'Telemetry Metric'}
              </h4>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-2.5 text-xs">
            <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">Source Classification</div>
              <div className="font-bold text-slate-900 mt-0.5 flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full inline-block shrink-0"
                  style={{ backgroundColor: info.color }}
                />
                {info.label}
              </div>
              <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                {info.description}
              </div>
            </div>

            {metricData?.definition && (
              <div>
                <span className="text-slate-400 font-medium">Definition:</span>
                <p className="text-slate-700 font-normal mt-0.5 leading-relaxed">
                  {metricData.definition}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100 text-[11px]">
              <div>
                <span className="text-slate-400 block font-medium">Provider:</span>
                <span className="text-slate-800 font-semibold">
                  {metricData?.providerLabel || metricData?.provider || 'Official Store APIs'}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block font-medium">Confidence:</span>
                <span className="text-slate-800 font-semibold">
                  {metricData?.confidence ? `${Math.round(metricData.confidence * 100)}%` : '100% Direct'}
                </span>
              </div>
            </div>

            {metricData?.modelVersion && (
              <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-[11px]">
                <span className="text-slate-400 font-medium">Model Version:</span>
                <span className="font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                  {metricData.modelVersion}
                </span>
              </div>
            )}

            {metricData?.periodStart && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-400 font-medium">Timeframe:</span>
                <span className="text-slate-700 font-medium">
                  {metricData.periodStart} {metricData.periodEnd ? `– ${metricData.periodEnd}` : ''}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-[10px] text-slate-400">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Audited & Sourced
              </span>
              <span>Rule 32 Enforced</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
